import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { EmployerLayout } from '../JobPostPricing/EmployerLayout';
import { useAssistantPage } from '../Applications/copilot/useAssistantPage';
import { Input } from '@/Components/ui/input';
import { Button } from '@/Components/ui/button';
import {
  Search,
  Send,
  MessageSquare,
  Loader2,
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  Phone,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  listConversations,
  getThread,
  sendReply,
} from './whatsappInboxApi';

const LIST_POLL_MS = 15000;
const THREAD_POLL_MS = 10000;

// Backend sends naive UTC (no tz suffix). Append 'Z' so JS treats it as UTC,
// then render in the viewer's local zone.
function parseUTC(iso) {
  if (!iso) return null;
  const s = /([zZ]|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function fmtTime(iso) {
  const d = parseUTC(iso);
  return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
}

function fmtListStamp(iso) {
  const d = parseUTC(iso);
  if (!d) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
}

function initials(name, phone) {
  if (name) {
    const parts = name.trim().split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
  }
  return (phone || '?').slice(-2);
}

function StatusTick({ status }) {
  if (status === 'failed') return <AlertCircle className="h-3.5 w-3.5 text-red-500" />;
  if (status === 'read') return <CheckCheck className="h-3.5 w-3.5 text-sky-500" />;
  if (status === 'delivered') return <CheckCheck className="h-3.5 w-3.5 text-muted-foreground" />;
  if (status === 'sent') return <Check className="h-3.5 w-3.5 text-muted-foreground" />;
  return <Clock className="h-3 w-3 text-muted-foreground" />;
}

// Render WhatsApp text formatting so *bold*, _italic_, ~strike~ and `mono` show
// styled instead of leaking their literal markers (*, _, ~) into the Inbox.
function renderWA(text) {
  if (!text) return null;
  const tokens = String(text).split(/(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|`[^`\n]+`)/g);
  return tokens.map((tok, i) => {
    if (!tok) return null;
    const paired = tok.length >= 2 && tok[0] === tok[tok.length - 1];
    const inner = tok.slice(1, -1);
    if (paired && tok[0] === '*') return <strong key={i}>{inner}</strong>;
    if (paired && tok[0] === '_') return <em key={i}>{inner}</em>;
    if (paired && tok[0] === '~') return <s key={i}>{inner}</s>;
    if (paired && tok[0] === '`')
      return (
        <code key={i} className="rounded bg-black/10 px-1 text-[0.85em]">
          {inner}
        </code>
      );
    return <React.Fragment key={i}>{tok}</React.Fragment>;
  });
}

// `scope` is fixed by the mount point, NOT toggled in the UI:
//   - company portal (HireWithUs) → default WhatsAppInbox (scope="company", wrapped in EmployerLayout)
//   - admin portal                → named WhatsAppInboxPanel scope="global" (renders inside the admin shell,
//                                    with no company layout)
// The backend independently enforces that "global" requires a super-admin.
export function WhatsAppInboxPanel({ scope = 'company' } = {}) {
  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [search, setSearch] = useState('');

  const [selected, setSelected] = useState(null); // phone
  const [thread, setThread] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);

  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const bottomRef = useRef(null);

  // ── Conversation list ────────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    try {
      const data = await listConversations({ scope, search });
      setConversations(data.conversations || []);
    } catch (e) {
      setConversations([]);
    } finally {
      setLoadingList(false);
    }
  }, [scope, search]);

  // Reload on scope change (immediate) + debounce on search.
  useEffect(() => {
    setLoadingList(true);
    const t = setTimeout(loadList, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [scope, search, loadList]);

  // Poll the list.
  useEffect(() => {
    const id = setInterval(loadList, LIST_POLL_MS);
    return () => clearInterval(id);
  }, [loadList]);

  // ── Thread ───────────────────────────────────────────────────────────────
  const loadThread = useCallback(async (phone, quiet = false) => {
    if (!phone) return;
    if (!quiet) setLoadingThread(true);
    try {
      const data = await getThread(phone);
      setThread(data);
    } catch (e) {
      if (!quiet) setThread(null);
    } finally {
      if (!quiet) setLoadingThread(false);
    }
  }, []);

  useEffect(() => {
    setThread(null);
    setSendError('');
    setReplyText('');
    if (selected) loadThread(selected);
  }, [selected, loadThread]);

  // Poll the open thread quietly.
  useEffect(() => {
    if (!selected) return;
    const id = setInterval(() => loadThread(selected, true), THREAD_POLL_MS);
    return () => clearInterval(id);
  }, [selected, loadThread]);

  // Auto-scroll to newest.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread?.messages?.length, selected]);

  // ── Send reply ─────────────────────────────────────────────────────────────
  const handleSend = async () => {
    const body = replyText.trim();
    if (!body || !selected || sending) return;
    setSending(true);
    setSendError('');
    try {
      const res = await sendReply(selected, body);
      setThread((t) => (t ? { ...t, messages: [...t.messages, res.message] } : t));
      setReplyText('');
      loadList();
    } catch (e) {
      const data = e?.response?.data;
      setSendError(data?.message || 'Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const onReplyKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canReply = thread?.can_free_reply;

  // AI Assistant (G2 page adapter): the open thread. No-op in the admin portal (no provider).
  const activeConversation = useMemo(
    () => (selected ? { phone: selected, name: thread?.name || null, candidate_email: thread?.candidate_email || null } : null),
    [selected, thread?.name, thread?.candidate_email]
  );
  useAssistantPage({ page: 'messages', activeConversation });

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">WhatsApp Messages</h1>
            {scope === 'global' && (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                All companies
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => loadList()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Two-pane body */}
        <div className="flex min-h-0 flex-1 overflow-hidden rounded-lg border border-border bg-background">
          {/* Left: conversation list */}
          <div
            className={cn(
              'flex w-full flex-col border-r border-border md:w-[340px] md:shrink-0',
              selected && 'hidden md:flex'
            )}
          >
            <div className="border-b border-border p-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name or number…"
                  className="pl-9"
                />
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {loadingList ? (
                <div className="flex items-center justify-center py-10 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                  No conversations yet.
                </div>
              ) : (
                conversations.map((c) => (
                  <button
                    key={c.phone}
                    onClick={() => setSelected(c.phone)}
                    className={cn(
                      'flex w-full items-center gap-3 border-b border-border/50 px-3 py-3 text-left transition-colors hover:bg-muted/60',
                      selected === c.phone && 'bg-muted'
                    )}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                      {initials(c.name, c.phone)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium">
                          {c.name || c.phone}
                        </span>
                        <span className="shrink-0 text-[11px] text-muted-foreground">
                          {fmtListStamp(c.last_at)}
                        </span>
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {c.last_direction === 'outbound' && (
                          <span className="mr-1 text-muted-foreground/70">You:</span>
                        )}
                        {c.last_preview}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right: thread */}
          <div className={cn('flex min-w-0 flex-1 flex-col', !selected && 'hidden md:flex')}>
            {!selected ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-2 text-muted-foreground">
                <MessageSquare className="h-10 w-10 opacity-30" />
                <p className="text-sm">Select a conversation to view messages</p>
              </div>
            ) : (
              <>
                {/* Thread header */}
                <div className="flex items-center gap-3 border-b border-border px-4 py-3">
                  <button
                    className="md:hidden"
                    onClick={() => setSelected(null)}
                    aria-label="Back"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    {initials(thread?.name, selected)}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {thread?.name || selected}
                    </div>
                    <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                      <Phone className="h-3 w-3" />
                      {selected}
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-muted/30 px-4 py-4">
                  {loadingThread ? (
                    <div className="flex items-center justify-center py-10 text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                  ) : (
                    (thread?.messages || []).map((m, i) => {
                      const out = m.direction === 'outbound';
                      return (
                        <div
                          key={m.id ?? i}
                          className={cn('flex', out ? 'justify-end' : 'justify-start')}
                        >
                          <div
                            className={cn(
                              'max-w-[78%] rounded-2xl px-3 py-2 text-sm shadow-sm',
                              out
                                ? 'rounded-br-sm bg-primary text-primary-foreground'
                                : 'rounded-bl-sm bg-background'
                            )}
                          >
                            {m.template_name && (
                              <div
                                className={cn(
                                  'mb-0.5 text-[10px] font-medium uppercase tracking-wide',
                                  out ? 'text-primary-foreground/70' : 'text-muted-foreground'
                                )}
                              >
                                {m.template_name}
                              </div>
                            )}
                            <div className="whitespace-pre-wrap break-words">
                              {m.body ? renderWA(m.body) : <span className="italic opacity-70">[no text]</span>}
                            </div>
                            <div
                              className={cn(
                                'mt-1 flex items-center justify-end gap-1 text-[10px]',
                                out ? 'text-primary-foreground/70' : 'text-muted-foreground'
                              )}
                            >
                              <span>{fmtTime(m.created_at)}</span>
                              {out && <StatusTick status={m.status} />}
                            </div>
                            {m.status === 'failed' && m.error_message && (
                              <div className="mt-1 text-[10px] text-red-200">
                                {m.error_message}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={bottomRef} />
                </div>

                {/* Reply box */}
                <div className="border-t border-border p-3">
                  {sendError && (
                    <div className="mb-2 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
                      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>{sendError}</span>
                    </div>
                  )}
                  {canReply ? (
                    <div className="flex items-end gap-2">
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={onReplyKeyDown}
                        rows={1}
                        placeholder="Type a reply…  (Enter to send, Shift+Enter for newline)"
                        className="max-h-32 min-h-[40px] flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
                        disabled={sending}
                      />
                      <Button onClick={handleSend} disabled={sending || !replyText.trim()} size="icon">
                        {sending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="rounded-md border border-border bg-muted/50 px-3 py-2.5 text-center text-xs text-muted-foreground">
                      You can only reply within 24&nbsp;hours of the candidate&apos;s last
                      message. Ask them to message first, or reach them with an approved
                      template from the automated flows.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
  );
}

// HireWithUs (company portal) mount — wraps the panel in the company sidebar
// layout and stays company-scoped. The admin portal imports WhatsAppInboxPanel
// directly with scope="global".
export default function WhatsAppInbox({ scope = 'company' } = {}) {
  return (
    <EmployerLayout contentClassName="max-w-none">
      <WhatsAppInboxPanel scope={scope} />
    </EmployerLayout>
  );
}
